import { createSelector } from 'reselect';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRadarConfig } from '../api/radarProfile';
import { createReducer } from '../reducers/factory';

const name = 'radarConfig';

const FETCH_SUCCESSFUL = `FETCH_${name}_SUCCESSFUL`;

const radarConfigReducer = createReducer('RADAR_USER_CONFIG', {
  fetchTimeout: 30 * 1000,
  invalidateActions: ['SET_LANGUAGE'],
  extendedReducer: {
    [FETCH_SUCCESSFUL]: (state, action) => ({
      ...state,
      fetchingInProgressSince: null,
      lastSuccessfulFetch: action.timestamp,
      fetchPromise: null,
      config: action.config,
      didInvalidate: false,
    }),
  },
  minUpdate: 30 * 60 * 1000,
  initialState: {
    config: {},
  },
});

radarConfigReducer.fetchSuccessful = (config) => ({
  type: FETCH_SUCCESSFUL,
  config,
  timestamp: Date.now(),
});

export const radarConfigPlainSelector = ({ [name]: radarConfig }) =>
  radarConfig;

const fetchRadarConfigAction = () => async (dispatch) => {
  const fetchPromise = (async () => {
    try {
      dispatch(radarConfigReducer.fetchSuccessful(await fetchRadarConfig()));
    } catch (e) {
      dispatch(radarConfigReducer.fetchFailed(e));
      throw e;
    }
  })();

  dispatch(radarConfigReducer.fetchInProgress(fetchPromise));
  return fetchPromise;
};

export const radarConfigSelector = createSelector(
  radarConfigPlainSelector,
  ({
    fetchingInProgressSince,
    lastSuccessfulFetch,
    lastFailedFetch,
    ...rest
  }) => ({
    ...rest,
    loading:
      !!fetchingInProgressSince || (!lastSuccessfulFetch && !lastFailedFetch),
  }),
);

export const fetchConfigIfNeeded = () => async (dispatch, getState) => {
  const { fetchPromise, config, ...state } = radarConfigPlainSelector(
    getState(),
  );
  if (radarConfigReducer.shouldFetch(state)) {
    return dispatch(fetchRadarConfigAction());
  }

  return typeof fetchPromise?.then === 'function' ? fetchPromise : config;
};

export const useRadarConfig = () => {
  const data = useSelector(radarConfigSelector);

  const dispatch = useDispatch();
  const { didInvalidate } = data;

  useEffect(() => {
    dispatch(fetchConfigIfNeeded()).catch(() => {});
  }, [dispatch, didInvalidate]);

  return data;
};

function RadarConfigReducer(
  state = radarConfigReducer.INITIAL_STATE,
  action = {},
) {
  return radarConfigReducer(state, action);
}

export default { [name]: RadarConfigReducer };
